import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '../../../api/projects';
import type { ProjectFilters as ProjectFiltersType } from '@spi/shared';
import { ProjectCard } from './ProjectCard';
import { ProjectFilters } from './ProjectFilters';
import { ProjectsMapView } from './ProjectsMapView';
import { Pagination } from '../shared/Pagination';
import { EmptyState } from '../shared/EmptyState';
import { PageMeta } from '../shared/PageMeta';
import { ProjectIcon } from '../shared/EntityIcons';

type ViewMode = 'list' | 'map';

export function ProjectsListPage() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || undefined;
  const [filters, setFilters] = useState<ProjectFiltersType>({ page: 1, limit: 12, search: initialSearch });
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const queryFilters = useMemo<ProjectFiltersType>(() => {
    if (viewMode === 'map') return { ...filters, page: 1, limit: 1000 };
    return filters;
  }, [filters, viewMode]);

  const { data, isLoading } = useProjects(queryFilters);

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title="Browse Projects" description="Browse seed donation projects from verified organizations worldwide" path="/marketplace/projects" />

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl flex items-center gap-2"><ProjectIcon className="w-7 h-7 text-spi-green" />Browse Projects</h2>
        <div className="flex items-center bg-spi-parchment rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list' ? 'bg-white text-spi-green shadow-sm' : 'text-spi-slate hover:text-spi-green'
            }`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
            </svg>
            List
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'map' ? 'bg-white text-spi-green shadow-sm' : 'text-spi-slate hover:text-spi-green'
            }`}
            title="Map view"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Map
          </button>
        </div>
      </div>

      <ProjectFilters filters={filters} onChange={setFilters} resultCount={data?.total} />

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
        </div>
      ) : viewMode === 'map' ? (
        <ProjectsMapView projects={data?.data ?? []} />
      ) : data?.data.length ? (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.data.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
          <Pagination page={data.page} totalPages={data.totalPages} onPageChange={(p) => setFilters({ ...filters, page: p })} />
        </>
      ) : (
        <EmptyState title="No projects found" description="Try adjusting your filters or check back later." />
      )}
    </div>
  );
}
